import React from 'react';
import styled from 'styled-components';
import useFeatureToggle from 'Client/hooks/useFeatureToggle';
import { BellaTestIds } from 'Common/qaIds';

const Badge = styled.span<{
    color: string;
    isClickable: boolean;
    height: number;
    fontSize: number;
    isSmallVariant: boolean;
}>`
    position: relative;
    white-space: nowrap;
    display: inline-flex;

    background-color: ${({ color }) => color};
    border-color: ${({ color }) => color};
    padding-left: 4px;

    height: ${({ height }) => `${height}px`};
    min-width: ${({ height }) => `${Math.ceil(1.125 * height)}px`};
    line-height: ${({ height }) => `${height}px`};
    font-size: ${({ fontSize }) => `${fontSize}px`};
    color: #fff;
    font-weight: ${({ isSmallVariant }) => (isSmallVariant ? '400' : '500')};

    cursor: ${({ isClickable }) => (isClickable ? 'pointer' : 'inherit')};

    &::after {
        content: '';

        position: absolute;
        left: 100%;

        border: ${({ height }) => `${height / 2}px solid transparent`};
        border-left: ${({ color }) => `8px solid ${color}`};
    }

    > sup {
        vertical-align: top;
        font-size: ${({ fontSize }) => `${Math.ceil(0.875 * fontSize)}px`};
        line-height: ${({ height }) => `${height}px`};
        top: ${({ isSmallVariant }) => (isSmallVariant ? '-2px' : '-3px')};
    }
`;

export type Props = {
    label?: string;
    color?: string;
    onClick?: (e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => void;
    className?: string;
    variant?: 'big' | 'small';
};

const EnergyEfficiencyBadge = ({
    label,
    color,
    onClick,
    className,
    variant = 'small',
}: Props): React.ReactElement | null => {
    const { p100EnergyEfficiencyLabel } = useFeatureToggle();
    const isButton = !!onClick;
    const additionalButtonProps = isButton
        ? {
              role: 'button',
              tabIndex: 0,
          }
        : {};
    if (!p100EnergyEfficiencyLabel || !label || !color) {
        return null;
    }

    let height = 16;
    let fontSize = 14;

    if (variant === 'big') {
        height = 20;
        fontSize = 18;
    }

    return (
        <Badge
            {...additionalButtonProps}
            color={color}
            onClick={onClick}
            isClickable={isButton}
            className={className}
            height={height}
            fontSize={fontSize}
            isSmallVariant={variant === 'small'}
            data-testid={BellaTestIds.energyEfficiencyLabel}
        >
            {label[0]}
            <sup>{label.slice(1)}</sup>
        </Badge>
    );
};

export default EnergyEfficiencyBadge;
